import { ROUTE_PATHS } from '../../library/helper'
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { styled, Tooltip, tooltipClasses } from "@mui/material";


export const types = {
    activatable: 'activatable',
    expandable: 'expandable',
    showable: 'showable',
}

const HtmlTooltip = styled(({ className, ...props }) => (
    <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
    [`& .${tooltipClasses.tooltip}`]: {
        backgroundColor: "#f5f5f9",
        color: "rgba(0, 0, 0, 0.87)",
        maxWidth: 280,
        fontSize: theme.typography.pxToRem(12),
        border: "2px solid #dadde9",
    },
}));


/* ---------- Components ---------- */
export const ActivableWithIcon = ({ to, id, icon, label }) => (
    <li className="nav-item">
        <Link className="nav-link menu-link" to={to} id={id}>
            <i className={icon}></i>
            <span>{label}</span>
        </Link>
    </li>
)

export const Activable = ({ to, id, label, detail }) => (
    <li className="nav-item" title={detail}>
        <Link to={to} className="nav-link" id={id}>
            {label}
        </Link>
    </li>
)

export const Showable = ({ parentId, id, children, custom }) => (
    <div
        className="collapse menu-dropdown"
        style={{ width: custom }}
        data-bs-parent={parentId}
        id={id}
    >
        <ul className="nav nav-sm flex-column">{children}</ul>
    </div>
)

export const Expandable = ({ childToToggle, parentId, id, label, children }) => (
    <li className="nav-item">
        <a
            data-bs-toggle="collapse"
            data-bs-target={`#${childToToggle}`}
            className="nav-link"
            href={`#${childToToggle}`}
            role="button"
            aria-expanded="false"
            aria-controls={childToToggle}
            data-bs-parent={parentId}
            id={id}
        >
            {label}
        </a>
        {children}
    </li>
)

export const ExpandableWithIcon = ({
    childToToggle,
    id,
    icon,
    label,
    children,
}) => (
    <li id={`nav-${id}`} className="nav-item">
        <a
            data-bs-toggle="collapse"
            data-bs-target={`#${childToToggle}`}
            className="nav-link menu-link"
            href={`#${childToToggle}`}
            role="button"
            aria-expanded="false"
            aria-controls={childToToggle}
            id={id}
        >
            <i className={icon}></i>
            <span>{label}</span>
        </a>
        {children}
    </li>
)

export const SoonMenu = ({ href, id, label }) => {
    const { t } = useTranslation()
    return (
        <li className="nav-item" style={{ opacity: 0.5 }}>
            <a
                href={href}
                className="nav-link"
                id={id}
                style={{ pointerEvents: 'none', cursor: 'default' }}
            >
                {label}&nbsp;
                <sup style={{ color: '#c7ece7' }}>{t('menu.soon')}</sup>
            </a>
        </li>
    )
}

export const DisabledMenu = ({ href, id, label, tooltip }) => {
    const { t } = useTranslation();

    return (
        <li className="nav-item" style={{ opacity: 0.5 }}>
            <a
                href={href}
                className="nav-link"
                id={id}
            >
                <HtmlTooltip
                    title={
                        <>
                            {t(tooltip || "menu.callToUpgrade")}
                        </>
                    }
                    arrow
                    placement="right"
                >
                    <span style={{ display: "inline-block" }}>{t(label)}</span>
                </HtmlTooltip>
                &nbsp;
            </a>
        </li>
    );
};

// One superset map to drive “active/expanded/show” behavior for any route
export const SIDEBAR_DATA = {
    [ROUTE_PATHS.dashboard]: [['sidebarDashboard', types.activatable]],

    [ROUTE_PATHS.emission]: [
        ['sidebarMeasure', types.expandable],
        ['sidebarMeasureChildren', types.showable],
        ['sidebarMeasureEmission', types.expandable],
        ['sidebarMeasureEmissionChildren', types.showable],
        ['sidebarMeasureEmissionScopeOneTwo', types.activatable],
    ],

    // Scope 3 categories (1..15) — keep full set; configs decide visibility
    ...Object.fromEntries(
        [1, 2, 3, 4, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15].map((n) => ([
            ROUTE_PATHS[`scope3category${n}`],
            [
                ['sidebarMeasure', types.expandable],
                ['sidebarMeasureChildren', types.showable],
                ['sidebarMeasureEmission', types.expandable],
                ['sidebarMeasureEmissionChildren', types.showable],
                ['sidebarMeasureEmissionScopeThree', types.expandable],
                ['sidebarMeasureEmissionScopeThreeChildren', types.showable],
                [`sidebarMeasureEmissionScopeThreeCategory${n}`, types.activatable],
            ]
        ]))
    ),

    [ROUTE_PATHS.revenue]: [
        ['sidebarMeasure', types.expandable],
        ['sidebarMeasureChildren', types.showable],
        ['sidebarMeasureRevenue', types.activatable],
    ],

    // Action
    [ROUTE_PATHS.reduction]: [
        ['sidebarAction', types.activatable],
    ],
    [ROUTE_PATHS.reductionCreate]: [
        ['sidebarAction', types.activatable],
    ],
    [ROUTE_PATHS.removal]: [
        ['sidebarAction', types.expandable],
        ['sidebarActionChildren', types.showable],
        ['sidebarActionRemoval', types.activatable],
    ],

    // Analytics
    [ROUTE_PATHS.scenario]: [
        ['sidebarAnalytic', types.expandable],
        ['sidebarAnalyticChildren', types.showable],
        ['sidebarAnalyticScenario', types.activatable],
    ],
    [ROUTE_PATHS.analyticsSBTi]: [
        ['sidebarAnalytic', types.expandable],
        ['sidebarAnalyticChildren', types.showable],
        ['sidebarAnalyticScenarioSBTi', types.activatable],
    ],
    [ROUTE_PATHS.analyticsCarbonIntensity]: [
        ['sidebarAnalytic', types.expandable],
        ['sidebarAnalyticChildren', types.showable],
        ['sidebarAnalyticCarbonIntensity', types.activatable],
    ],
    [ROUTE_PATHS.analyticsCompletion]: [
        ['sidebarAnalytic', types.expandable],
        ['sidebarAnalyticChildren', types.showable],
        ['sidebarAnalyticCompletion', types.activatable],
    ],
    [ROUTE_PATHS.analyticsLocation]: [
        ['sidebarAnalytic', types.expandable],
        ['sidebarAnalyticChildren', types.showable],
        ['sidebarAnalyticLocation', types.activatable],
    ],
    [ROUTE_PATHS.analyticsLocationSummary]: [
        ['sidebarAnalytic', types.expandable],
        ['sidebarAnalyticChildren', types.showable],
        ['sidebarAnalyticLocationSummary', types.activatable],
    ],

    // Reports
    [ROUTE_PATHS.reportIso]: [
        ['sidebarReport', types.expandable],
        ['sidebarReportChildren', types.showable],
        ['sidebarReportISO', types.activatable],
    ],
    [ROUTE_PATHS.reportGhg]: [
        ['sidebarReport', types.expandable],
        ['sidebarReportChildren', types.showable],
        ['sidebarReportGHG', types.activatable],
    ],
    [ROUTE_PATHS.reportGri]: [
        ['sidebarReport', types.expandable],
        ['sidebarReportChildren', types.showable],
        ['sidebarReportGRI', types.activatable],
    ],
    [ROUTE_PATHS.reportSummary]: [
        ['sidebarReport', types.expandable],
        ['sidebarReportChildren', types.showable],
        ['sidebarReportSummary', types.activatable],
    ],

    // Settings
    [ROUTE_PATHS.setting]: [
        ['sidebarSetting', types.expandable],
        ['sidebarSettingChildren', types.showable],
        ['sidebarSettingGeneral', types.activatable],
    ],
    [ROUTE_PATHS.settingOrganizationInformation]: [
        ['sidebarSetting', types.expandable],
        ['sidebarSettingChildren', types.showable],
        ['sidebarSettingOrganizationInformation', types.activatable],
    ],
    [ROUTE_PATHS.settingUsers]: [
        ['sidebarSetting', types.expandable],
        ['sidebarSettingChildren', types.showable],
        ['sidebarSettingUsers', types.activatable],
    ],
    [ROUTE_PATHS.settingScenario]: [
        ['sidebarSetting', types.expandable],
        ['sidebarSettingChildren', types.showable],
        ['sidebarSettingScenario', types.activatable],
    ],
    [ROUTE_PATHS.settingCarbonIntensity]: [
        ['sidebarSetting', types.expandable],
        ['sidebarSettingChildren', types.showable],
        ['sidebarSettingCarbonIntensity', types.activatable],
    ],
    [ROUTE_PATHS.settingRevenue]: [
        ['sidebarSetting', types.expandable],
        ['sidebarSettingChildren', types.showable],
        ['sidebarSettingAdvanced', types.expandable],
        ['sidebarSettingAdvancedChildren', types.showable],
        ['sidebarSettingAdvancedRevenue', types.activatable],
    ],
    [ROUTE_PATHS.division]: [
        ['sidebarSetting', types.expandable],
        ['sidebarSettingChildren', types.showable],
        ['sidebarSettingAdvanced', types.expandable],
        ['sidebarSettingAdvancedChildren', types.showable],
        ['sidebarSettingAdvancedDivision', types.activatable],
    ],
    [ROUTE_PATHS.company]: [
        ['sidebarSetting', types.expandable],
        ['sidebarSettingChildren', types.showable],
        ['sidebarSettingAdvanced', types.expandable],
        ['sidebarSettingAdvancedChildren', types.showable],
        ['sidebarSettingAdvancedCompany', types.activatable],
    ],
    [ROUTE_PATHS.location]: [
        ['sidebarSetting', types.expandable],
        ['sidebarSettingChildren', types.showable],
        ['sidebarSettingAdvanced', types.expandable],
        ['sidebarSettingAdvancedChildren', types.showable],
        ['sidebarSettingAdvancedLocation', types.activatable],
    ],
    [ROUTE_PATHS.user]: [
        ['sidebarSetting', types.expandable],
        ['sidebarSettingChildren', types.showable],
        ['sidebarSettingAdvanced', types.expandable],
        ['sidebarSettingAdvancedChildren', types.showable],
        ['sidebarSettingAdvancedUser', types.activatable],
    ],
    '/setting/advanced/emission-sources': [
        ['sidebarSetting', types.expandable],
        ['sidebarSettingChildren', types.showable],
        ['sidebarSettingAdvanced', types.expandable],
        ['sidebarSettingAdvancedChildren', types.showable],
        ['sidebarSettingAdvancedEmissionSources', types.activatable],
    ],
}

export const getActiveRootPath = (pathname) => {
    if (!pathname) return '/'
    const parts = pathname.split('/')
    let root = `/${parts[1]}`
    if (root === '/scope3') root = `/${parts[1]}/${parts[2]}`
    if (`/${parts[1]}/${parts[2]}/${parts[3]}` === ROUTE_PATHS.settingRevenue) {
        root = ROUTE_PATHS.settingRevenue
    }
    return root
}

export const DIRECT_MATCHES = new Set([
    ROUTE_PATHS.analyticsLocation,
    ROUTE_PATHS.analyticsSBTi,
    ROUTE_PATHS.analyticsLocationSummary,
    ROUTE_PATHS.analyticsCarbonIntensity,
    ROUTE_PATHS.reportIso,
    ROUTE_PATHS.reportGhg,
    ROUTE_PATHS.reportGri,
    ROUTE_PATHS.reportSummary,
    ROUTE_PATHS.settingScenario,
    ROUTE_PATHS.settingCarbonIntensity,
    ROUTE_PATHS.settingRevenue,
    ROUTE_PATHS.division,
    ROUTE_PATHS.company,
    ROUTE_PATHS.location,
    ROUTE_PATHS.user,
    ROUTE_PATHS.setting,
    ROUTE_PATHS.settingOrganizationInformation,
    ROUTE_PATHS.settingUsers,
    '/setting/advanced/emission-sources',
])
